import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from '@mantine/core';
import { IoSparklesOutline } from 'react-icons/io5';
import { OptionsTable, type OptionsTableColumn } from '@components';
import { Button, CachedImage, Input, RefetchButton, Select, Tooltip } from '@shared/ui';
import { useCategories } from '@modules/categories';
import { usePickupPoints } from '@modules/pickup-points';
import {
  useSellerSearchPreview,
  useSellerPromotionCities,
  type SellerSearchPreviewRow,
} from '@modules/products';

type SearchPreviewTableRow = SellerSearchPreviewRow & { id: number };

function normalizeCityKey(city: string): string {
  return city.trim().toLowerCase();
}

function formatRating(value: number | null): string {
  const rating = value != null && Number.isFinite(value) ? value : 0;
  return rating.toLocaleString('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function formatRank(value: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
}

function formatCpc(value: number | null, enabled: boolean): string {
  if (!enabled || value == null || value <= 0) {
    return '—';
  }
  const rub = value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  return `${rub} ₽`;
}

function ProductThumb({ imageUrl }: { imageUrl: string | null }) {
  return (
    <div className="seller-promotion-table__thumb">
      {imageUrl ? (
        <CachedImage src={imageUrl} alt="" loading="lazy" />
      ) : (
        <span className="seller-promotion-table__thumb-placeholder">—</span>
      )}
    </div>
  );
}

function NewMark() {
  return (
    <Tooltip label="Новинка" withArrow position="top">
      <span className="seller-search-preview__new" aria-label="Новинка">
        <IoSparklesOutline size={18} aria-hidden />
      </span>
    </Tooltip>
  );
}

const numericColStyle = {
  whiteSpace: 'nowrap' as const,
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums' as const,
};

const searchPreviewColumns: OptionsTableColumn<SearchPreviewTableRow>[] = [
  {
    key: 'rowNumber',
    label: 'Место',
    style: { width: 64, ...numericColStyle, textAlign: 'center' },
    render: (row) => row.searchPosition,
  },
  {
    key: 'imageUrl',
    label: 'Фото',
    style: { width: 56 },
    render: (row) => <ProductThumb imageUrl={row.imageUrl} />,
  },
  {
    key: 'name',
    label: 'Название',
    render: (row) => (
      <span className="seller-search-preview__name">
        {row.isOwnProduct ? (
          <Link
            to={`/products/${row.productId}`}
            state={{ from: '/promotion', promotionTab: 'search' }}
            style={{ color: 'inherit' }}
          >
            {row.name}
          </Link>
        ) : (
          row.name
        )}
        {row.isNew ? <NewMark /> : null}
      </span>
    ),
  },
  {
    key: 'shopName',
    label: 'Магазин',
    render: (row) => row.shopName ?? (row.sellerId == null ? 'Маркетплейс' : '—'),
  },
  {
    key: 'cpcRub',
    label: 'CPC, ₽',
    style: numericColStyle,
    render: (row) => formatCpc(row.cpcRub, row.promotionEnabled),
  },
  {
    key: 'orders30d',
    label: 'Заказы, 30 дн.',
    style: numericColStyle,
    render: (row) => row.orders30d,
  },
  {
    key: 'rating',
    label: 'Рейтинг',
    style: numericColStyle,
    render: (row) => formatRating(row.rating),
  },
  {
    key: 'precomputedRank',
    label: 'Ранг',
    style: numericColStyle,
    render: (row) => formatRank(row.precomputedRank),
  },
];

export default function SellerSearchPreviewTab() {
  const { data: categories = [] } = useCategories();
  const { data: citiesData } = useSellerPromotionCities();
  const { data: pickupPoints = [] } = usePickupPoints();
  const defaultCityAppliedRef = useRef(false);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [filters, setFilters] = useState<{
    keyword: string;
    categoryId: number | null;
    cityKey: string | null;
  }>({
    keyword: '',
    categoryId: null,
    cityKey: null,
  });
  const [categoryDraft, setCategoryDraft] = useState<string | null>(null);
  const [cityDraft, setCityDraft] = useState<string | null>(null);

  useEffect(() => {
    if (defaultCityAppliedRef.current || pickupPoints.length === 0) {
      return;
    }
    const firstCityKey = normalizeCityKey(pickupPoints[0].city);
    if (!firstCityKey) {
      return;
    }
    defaultCityAppliedRef.current = true;
    setCityDraft(firstCityKey);
    setFilters((prev) =>
      prev.cityKey == null ? { ...prev, cityKey: firstCityKey } : prev,
    );
  }, [pickupPoints]);

  const { data, isLoading, isFetching, error, refetch } = useSellerSearchPreview(
    {
      keyword: filters.keyword,
      categoryId: filters.categoryId,
      cityKey: filters.cityKey,
    },
  );

  const categoryOptions = useMemo(
    () =>
      categories.map((cat) => ({
        value: String(cat.id),
        label: cat.name,
      })),
    [categories],
  );

  const cityOptions = useMemo(
    () =>
      (citiesData?.cities ?? []).map((city) => ({
        value: city.cityKey,
        label: city.cityName,
      })),
    [citiesData?.cities],
  );

  const applyFilters = () => {
    setFilters({
      keyword: keywordDraft.trim(),
      categoryId: categoryDraft ? Number(categoryDraft) : null,
      cityKey: cityDraft,
    });
  };

  const items = data?.items ?? [];
  const searchPreviewData = useMemo(
    () => items.map((row) => ({ ...row, id: row.productId })),
    [items],
  );
  const hasKeyword = Boolean(filters.keyword);

  return (
    <>
      <div className="seller-search-preview__filters">
        <div className="seller-search-preview__field">
          <label className="seller-search-preview__label" htmlFor="seller-search-city">
            Город
          </label>
          <Select
            id="seller-search-city"
            className="seller-search-preview__control"
            classNames={{
              dropdown: 'seller-search-preview__dropdown',
              option: 'seller-search-preview__option',
            }}
            placeholder="Выберите город"
            searchable
            data={cityOptions}
            value={cityDraft}
            onChange={(value) => {
              if (value != null) {
                setCityDraft(String(value));
              }
            }}
          />
        </div>
        <div className="seller-search-preview__field seller-search-preview__field--query">
          <label className="seller-search-preview__label" htmlFor="seller-search-query">
            Поисковый запрос
          </label>
          <Input
            id="seller-search-query"
            className="seller-search-preview__control"
            placeholder="красные розы"
            value={keywordDraft}
            onChange={(e) => setKeywordDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                applyFilters();
              }
            }}
          />
        </div>
        <div className="seller-search-preview__field">
          <label className="seller-search-preview__label" htmlFor="seller-search-category">
            Категория
          </label>
          <Select
            id="seller-search-category"
            className="seller-search-preview__control"
            classNames={{
              dropdown: 'seller-search-preview__dropdown',
              option: 'seller-search-preview__option',
            }}
            placeholder="Все категории"
            clearable
            searchable
            data={categoryOptions}
            value={categoryDraft}
            onChange={(value) => setCategoryDraft(value != null ? String(value) : null)}
          />
        </div>
        <Button
          className="seller-search-preview__submit"
          onClick={applyFilters}
          loading={isFetching}
        >
          Показать
        </Button>
      </div>

      <div className="content-wrapper seller-promotion-tab-body">
        <div className="seller-promotion-scroll">
          <div className="seller-promotion-desktop-table">
            <OptionsTable
              columns={searchPreviewColumns}
              data={searchPreviewData}
              loading={isLoading || isFetching}
              error={error}
              refetch={refetch}
              emptyMessage={
                hasKeyword ? 'Нет товаров по этому запросу' : 'Нет товаров'
              }
            />
          </div>

          <div
            className="seller-promotion-mobile-list"
            aria-label="Результаты поиска"
          >
            {error && refetch ? (
              <RefetchButton
                refetch={refetch}
                isLoading={isLoading || isFetching}
                errorMessage={
                  error instanceof Error
                    ? error.message
                    : 'Не удалось загрузить данные'
                }
              />
            ) : isLoading || isFetching ? (
              <div className="empty-container">
                <Loader />
              </div>
            ) : searchPreviewData.length === 0 ? (
              <div className="empty-container">
                <p>
                  {hasKeyword ? 'Нет товаров по этому запросу' : 'Нет товаров'}
                </p>
              </div>
            ) : (
              searchPreviewData.map((row) => (
                <article
                  key={row.productId}
                  className="seller-promotion-mobile-card seller-promotion-mobile-card--search"
                >
                  <div className="seller-promotion-mobile-card__top">
                    <ProductThumb imageUrl={row.imageUrl} />
                    <div className="seller-promotion-mobile-card__main">
                      <span className="seller-search-preview__name">
                        {row.isOwnProduct ? (
                          <Link
                            to={`/products/${row.productId}`}
                            state={{ from: '/promotion', promotionTab: 'search' }}
                            className="seller-promotion-mobile-card__name"
                          >
                            {row.name}
                          </Link>
                        ) : (
                          <span className="seller-promotion-mobile-card__name">
                            {row.name}
                          </span>
                        )}
                        {row.isNew ? <NewMark /> : null}
                      </span>
                      <span className="seller-promotion-mobile-card__shop">
                        {row.shopName ??
                          (row.sellerId == null ? 'Маркетплейс' : '—')}
                      </span>
                    </div>
                    <span className="seller-promotion-mobile-card__place">
                      #{row.searchPosition}
                    </span>
                  </div>
                  <div className="seller-promotion-mobile-card__metrics">
                    <div className="seller-promotion-mobile-card__metric">
                      <span className="seller-promotion-mobile-card__metric-label">
                        CPC
                      </span>
                      <span className="seller-promotion-mobile-card__metric-value">
                        {formatCpc(row.cpcRub, row.promotionEnabled)}
                      </span>
                    </div>
                    <div className="seller-promotion-mobile-card__metric">
                      <span className="seller-promotion-mobile-card__metric-label">
                        Заказы, 30 дн.
                      </span>
                      <span className="seller-promotion-mobile-card__metric-value">
                        {row.orders30d}
                      </span>
                    </div>
                    <div className="seller-promotion-mobile-card__metric">
                      <span className="seller-promotion-mobile-card__metric-label">
                        Рейтинг
                      </span>
                      <span className="seller-promotion-mobile-card__metric-value">
                        {formatRating(row.rating)}
                      </span>
                    </div>
                    <div className="seller-promotion-mobile-card__metric">
                      <span className="seller-promotion-mobile-card__metric-label">
                        Ранг
                      </span>
                      <span className="seller-promotion-mobile-card__metric-value">
                        {formatRank(row.precomputedRank)}
                      </span>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
